// External imports
import React from 'react';
import { Button, Divider, Grid, Paper } from '@material-ui/core';
import {
  BUSINESS_SETUP_PAGES,
  EXTENDED_SETUP_PAGES,
  REQUIRED_SETUP_PAGES,
} from '~/app/constants.js';
import WebHelpers from '~/app/webHelpers.js';
import { checkOfficeLicense, canAccessCorp, determineProductCodeType } from '~/app/Utility/general';
// Redux imports
import { useSelector } from 'react-redux';
// Styling imports
import { styles } from '~/app/Pages/Setup/LicencingSetup/css/licencingSetup.js';

const LicencingSetup = props => {
  const payload = WebHelpers.getJWTPayload();
  const { officeProfile } = useSelector(state => ({
    officeProfile: state.officeProfile,
  }));

  // Resolve inherited display rows separately for 1040 and business licenses.
  const isBusinessLicense = license => ['COBU', 'COBX'].includes(license.productCode);

  const setupInhertedLicense = (license, season, isInherited = false, rowKey) => {
    let statusStyle = styles.inActiveBubbleText;
    let statusText = 'InActive';

    if (license.status) {
      statusStyle = styles.activeBubbleText;
      statusText = 'Active';
    }

    const corpProductCodeType = determineProductCodeType(license.productCode);
    const productLabel = `${license.productCode} ${corpProductCodeType}`.trim();

    // Keep the source product and season visible; the SEASON column shows the viewed season.
    const licenseLabel = isInherited
      ? `${productLabel} - Inherited from ${license.season}`
      : productLabel;

    return (
      <React.Fragment key={rowKey}>
        <Grid item xs={2} style={styles.gridColumnData}>
          {season}
        </Grid>
        <Grid item xs={4} style={styles.gridColumnData}>
          {license.licenseID}
        </Grid>
        <Grid item xs={3} style={styles.gridColumnData}>
          {licenseLabel}
        </Grid>
        <Grid item xs={3} style={styles.gridColumnData}>
          <span style={statusStyle}>{statusText}</span>
        </Grid>
      </React.Fragment>
    );
  };

  // createLicenseTable - return the sub table based on data passed in to props.licencingSetup
  const createLicenseTable = () => {
    if (!props.licencingSetup) {
      return;
    }

    const table = [];
    props.licencingSetup.map((license, index) => {
      table.push(setupInhertedLicense(license, license.season, false, `assigned-${index}`));
      return table;
    });

    // Business licensing requires assigned or inherited 1040 coverage.
    if (officeProfile.has1040License || officeProfile.hasSubsequent1040License) {
      if (officeProfile) {
        const licenseObj = checkOfficeLicense(officeProfile);
        officeProfile.oldestLicenseSeason = licenseObj.oldestLicenseSeason;
      }

      // Before the earliest assigned season, display the earliest license of each type.
      if (payload.season < officeProfile.oldestLicenseSeason) {
        // Neither product type has an assigned row for the selected season.
        const earliestLicenseOfType = licenseType =>
          officeProfile.licenseInfo
            .filter(license => (isBusinessLicense(license) ? 'business' : '1040') === licenseType)
            .reduce(
              (earliest, license) =>
                !earliest || license.season < earliest.season ? license : earliest,
              null,
            );

        ['1040', 'business'].forEach(licenseType => {
          const license = earliestLicenseOfType(licenseType);
          if (license) {
            table.push(
              setupInhertedLicense(license, payload.season, true, `inherited-${licenseType}`),
            );
          }
        });
      }
      // Otherwise, display next-season inheritance only for types without an assigned row.
      else {
        const assignedLicenseTypes = props.licencingSetup.map(license =>
          isBusinessLicense(license) ? 'business' : '1040',
        );

        officeProfile.licenseInfo.map((license, index) => {
          // Select a next-season source for an unassigned product type.
          const inheritedLicenseType = isBusinessLicense(license) ? 'business' : '1040';
          if (
            license.season === payload.season + 1 &&
            !assignedLicenseTypes.includes(inheritedLicenseType)
          ) {
            table.push(setupInhertedLicense(license, payload.season, true, `inherited-${index}`));
          }
          return table;
        });
      }
    }

    return table;
  };

  const handleNext = () => {
    let page = EXTENDED_SETUP_PAGES.EFILING.NAME;

    if (canAccessCorp(payload, officeProfile, props.currentView)) {
      page = BUSINESS_SETUP_PAGES.CLIENT_LETTERS.NAME;
    }
    props.loadSetupComponent(page);
  };

  return (
    <div className="container-fixed" style={{ marginTop: '2.5rem' }}>
      <Paper>
        <form style={{ marginLeft: '3vw', marginRight: '3vw', marginTop: '1vh' }}>
          <div
            style={{
              textAlign: 'left',
              paddingTop: '3vh',
              paddingBottom: '1vh',
            }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Grid container spacing={8} style={styles.parentGridContainer}>
              <Grid item xs={12} style={styles.paperTitle}>
                Office Licenses
              </Grid>
              <Grid item xs={12} style={styles.gridDivider}>
                <Divider />
              </Grid>
              <Grid item xs={12} style={styles.gridTableHeader}>
                License History
              </Grid>
              <Grid item xs={12}>
                <Grid container spacing={8} style={styles.gridSubTable}>
                  <Grid item xs={2} style={styles.gridColumnHeader}>
                    SEASON
                  </Grid>
                  <Grid item xs={4} style={styles.gridColumnHeader}>
                    LICENSE ID
                  </Grid>
                  <Grid item xs={3} style={styles.gridColumnHeader}>
                    LICENSE TYPE
                  </Grid>
                  <Grid item xs={3} style={styles.gridColumnHeader}>
                    STATUS
                  </Grid>
                  <Grid item xs={12} style={styles.tableDivider}>
                    <Divider />
                  </Grid>

                  {/* Loop over the data */}
                  {createLicenseTable()}
                </Grid>
              </Grid>

              {/* Bottom Previous and Next */}
              <Grid item xs={12}>
                <Grid container spacing={8} style={styles.footerGridContainer}>
                  <Grid item xs={12} style={styles.footerDivider}>
                    <Divider />
                  </Grid>
                  <Grid item xs={10}>
                    {/** Just a spacer to push the buttons right */}
                  </Grid>
                  <Grid item xs={1}>
                    <Button
                      id="btnLicencingSetupPrev"
                      color="primary"
                      onClick={() => props.loadSetupComponent(REQUIRED_SETUP_PAGES.BILLING.NAME)}
                    >
                      Previous
                    </Button>
                  </Grid>
                  <Grid item xs={1}>
                    <Button id="btnLicencingSetupNext" color="primary" onClick={handleNext}>
                      Next
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </div>
        </form>
      </Paper>
    </div>
  );
};

export default LicencingSetup;
